if (!customElements.get('sc-size-guide')) {
  customElements.define('sc-size-guide', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.pickerObserver?.disconnect();
      this.controller = new AbortController();
      this.trigger = this.querySelector('[data-sc-guide-open]');
      this.dialog = this.querySelector('[data-sc-guide-dialog]');
      this.section = document.getElementById(`shopify-section-${this.dataset.sectionId}`) || this.closest('.shopify-section');
      if (!this.trigger || !this.dialog || !this.section) return;

      const options = { signal: this.controller.signal };
      this.trigger.addEventListener('click', () => this.open(), options);
      this.querySelector('[data-sc-guide-close]')?.addEventListener('click', () => this.requestClose(), options);
      this.dialog.addEventListener('click', event => {
        const row = event.target.closest?.('[data-sc-guide-row]');
        if (row && this.dialog.contains(row)) {
          this.selectSize(row);
          return;
        }
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
          this.requestClose();
        }
      }, options);
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.requestClose();
      }, options);
      this.dialog.addEventListener('close', () => this.afterClose(), options);
      document.addEventListener('sc:size-guide-request-close', event => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.requestClose(event.detail.reason === 'add-to-cart');
      }, options);

      const sync = event => {
        const picker = event.target.closest?.('variant-picker');
        if (picker && this.section.contains(picker)) this.syncRows();
      };
      this.section.addEventListener('change', sync, options);
      this.section.addEventListener('variant:selected', sync, options);
      this.section.addEventListener('variant:updated', sync, options);
      this.pickerObserver = new MutationObserver(() => this.syncRows());
      const picker = this.nativePicker;
      if (picker) this.pickerObserver.observe(picker, { childList: true, subtree: true, attributes: true, attributeFilter: ['checked', 'selected', 'disabled', 'aria-disabled'] });
      this.syncRows();
    }

    get nativePicker() {
      return Array.from(this.section?.querySelectorAll('variant-picker') || []).find(picker => !picker.closest('product-card, quick-add-dialog'));
    }

    get sizeChoices() {
      const picker = this.nativePicker;
      const name = `option-${this.dataset.sizeOptionName}`;
      if (!picker || !this.dataset.sizeOptionName) return [];
      const radios = Array.from(picker.querySelectorAll('input[type="radio"]')).filter(input => input.name === name);
      if (radios.length) return radios;
      const select = Array.from(picker.querySelectorAll('select')).find(input => input.name === name);
      return select ? Array.from(select.options) : [];
    }

    canonicalSize(value) {
      return String(value).normalize('NFKC').trim().toUpperCase();
    }

    normalizedSize(value) {
      const label = this.canonicalSize(value).replace(/[‐‑‒–—−]/g, '-').replace(/\s*-\s*/g, '-').replace(/\s+/g, ' ');
      const extended = /^([2-9])X([LS])$/.exec(label);
      return extended ? `${'X'.repeat(Number(extended[1]))}${extended[2]}` : label;
    }

    choiceFor(label) {
      const choices = this.sizeChoices;
      const exact = choices.filter(choice => this.canonicalSize(choice.value) === this.canonicalSize(label));
      if (exact.length === 1) return exact[0];
      if (exact.length > 1) return null;
      const normalized = choices.filter(choice => this.normalizedSize(choice.value) === this.normalizedSize(label));
      return normalized.length === 1 ? normalized[0] : null;
    }

    isDisabled(choice) {
      return !choice || choice.disabled || choice.getAttribute('aria-disabled') === 'true' || choice.parentElement?.disabled === true;
    }

    syncRows() {
      let hasSelectable = false;
      for (const row of this.querySelectorAll('[data-sc-guide-row]')) {
        const button = row.querySelector('[data-sc-guide-select]');
        const choice = this.choiceFor(row.dataset.sizeLabel);
        const selected = Boolean(choice && (choice instanceof HTMLOptionElement ? choice.selected : choice.checked));
        const selectable = !this.isDisabled(choice);
        row.toggleAttribute('data-selected', selected);
        row.toggleAttribute('data-selectable', selectable);
        button.disabled = !selectable;
        button.setAttribute('aria-pressed', String(selected));
        hasSelectable ||= selectable;
      }
      const help = this.querySelector('[data-sc-guide-selection-help]');
      if (help) help.hidden = !hasSelectable;
    }

    selectSize(row) {
      if (this.isClosing) return;
      const choice = this.choiceFor(row.dataset.sizeLabel);
      if (this.isDisabled(choice)) return;
      if (choice instanceof HTMLOptionElement) {
        const select = choice.closest('select');
        if (!select) return;
        if (select.value !== choice.value) {
          select.value = choice.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        choice.click();
      }
      this.syncRows();
      this.dialog.dataset.scGuideSelected = 'true';
      const status = this.querySelector('[data-sc-guide-status]');
      if (status) status.textContent = (this.dataset.selectionMessage || '').replace('__SIZE__', choice.value);
      this.notify('sc:size-guide-selection', { size: choice.value });
    }

    open() {
      if (this.dialog.open) return;
      clearTimeout(this.closeTimer);
      this.isClosing = false;
      this.closeNotified = false;
      this.dialog.removeAttribute('data-closing');
      this.dialog.removeAttribute('data-sc-guide-selected');
      this.syncRows();
      this.dialog.showModal();
      this.wasOpen = true;
      document.documentElement.classList.add('sc-size-guide-is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.notify('sc:size-guide-open');
    }

    requestClose(immediate = false) {
      if (!this.dialog?.open) return;
      if (this.isClosing && !immediate) return;
      clearTimeout(this.closeTimer);
      this.isClosing = true;
      this.dialog.removeAttribute('data-sc-guide-selected');
      if (!this.closeNotified) {
        this.closeNotified = true;
        this.notify('sc:size-guide-close');
      }
      if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.dialog.close();
        return;
      }
      this.dialog.setAttribute('data-closing', '');
      this.closeTimer = setTimeout(() => {
        if (this.dialog.open) this.dialog.close();
      }, 500);
    }

    afterClose() {
      this.updateScrollLock();
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (!this.wasOpen) return;
      this.wasOpen = false;
      this.isClosing = false;
      this.dialog.removeAttribute('data-closing');
      this.dialog.removeAttribute('data-sc-guide-selected');
      if (!this.closeNotified) this.notify('sc:size-guide-close');
      this.closeNotified = false;
      if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
    }

    updateScrollLock() {
      document.documentElement.classList.toggle('sc-size-guide-is-open', Boolean(document.querySelector('sc-size-guide [data-sc-guide-dialog][open]')));
    }

    notify(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(name, { detail: { sectionId: this.dataset.sectionId, ...detail } }));
    }

    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      if (this.dialog?.open) this.dialog.close();
      this.afterClose();
      this.controller?.abort();
      this.pickerObserver?.disconnect();
    }
  });
}
